import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowUpRight, Instagram, MapPin, Phone } from 'lucide-react';
import Header from '../components/Header';
import SEOHead, { breadcrumbSchema } from '../components/SEOHead';
import { Reveal } from '../components/motion/Reveal';
import { Magnetic } from '../components/motion/Magnetic';
import { trackVendorClick, VendorCard } from './VendorsPage';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function VendorDetailPage() {
  const { slug } = useParams();
  const [vendor, setVendor] = useState(null);
  const [others, setOthers] = useState([]);
  const [settings, setSettings] = useState({});
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    setStatus('loading');
    Promise.all([
      fetch(`${API_URL}/api/vendors/${slug}`),
      fetch(`${API_URL}/api/vendors`).then((r) => r.json()).catch(() => []),
      fetch(`${API_URL}/api/content/settings`).then((r) => r.json()).catch(() => ({})),
    ]).then(async ([res, all, s]) => {
      setSettings(s || {});
      if (!res.ok) { setStatus('missing'); return; }
      const v = await res.json();
      setVendor(v);
      setOthers((all || []).filter((o) => o.id !== v.id).slice(0, 3));
      setStatus('ready');
    }).catch(() => setStatus('missing'));
  }, [slug]);

  if (status === 'loading') return <main className="bg-white min-h-screen flex items-center justify-center"><p className="text-xs tracking-[0.3em] uppercase text-black/30">Loading…</p></main>;
  if (status === 'missing') {
    return (
      <main className="bg-white min-h-screen flex items-center justify-center text-center px-6" data-testid="vendor-not-found">
        <SEOHead title="Vendor Not Found" noindex />
        <div><h1 className="font-presti text-4xl mb-6">Vendor not found</h1><Link to="/vendors" className="text-xs tracking-[0.25em] uppercase border-b border-black/20 pb-1">All vendors</Link></div>
      </main>
    );
  }

  const website = vendor.website_url ? (vendor.website_url.startsWith('http') ? vendor.website_url : `https://${vendor.website_url}`) : '';
  const schema = {
    '@context': 'https://schema.org', '@type': 'LocalBusiness', name: vendor.name, description: vendor.short_description,
    url: website || undefined, image: vendor.image_url || undefined, telephone: vendor.phone || undefined,
    address: vendor.location ? { '@type': 'PostalAddress', addressLocality: vendor.location } : undefined,
  };

  return (
    <main className="bg-white min-h-screen text-black" data-testid="vendor-detail-page">
      <SEOHead
        title={`${vendor.name} — Trusted ${vendor.categories?.[0] || 'Wedding Vendor'}`}
        description={vendor.short_description || `${vendor.name} is a trusted wedding vendor recommended by Crystal Media.`}
        url={`/vendors/${vendor.slug}`}
        image={vendor.image_url}
        siteSettings={settings}
        jsonLd={[breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Vendors', path: '/vendors' }, { name: vendor.name, path: `/vendors/${vendor.slug}` }]), schema]}
      />
      <Header logoUrl={settings.photo_logo_url || settings.logo_url || ''} variant="light" />

      <section className="pt-32 md:pt-40 pb-16 px-6 md:px-12">
        <div className="max-w-6xl mx-auto">
          <Reveal><Link to="/vendors" className="inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-black/40 hover:text-black mb-10" data-testid="vendor-back-link"><ArrowLeft size={12} /> All vendors</Link></Reveal>
          <div className="grid grid-cols-12 gap-8 lg:gap-14">
            <Reveal y={40} className="col-span-12 lg:col-span-6">
              <div className="aspect-[4/3] bg-[#f0f0f0] overflow-hidden">
                {vendor.image_url && <img src={vendor.image_url} alt={vendor.name} className="w-full h-full object-cover" decoding="async" />}
              </div>
              {vendor.gallery_urls?.length > 0 && (
                <div className="grid grid-cols-3 gap-3 mt-3">
                  {vendor.gallery_urls.slice(0, 6).map((u, i) => <img key={i} src={u} alt="" loading="lazy" className="aspect-square object-cover" />)}
                </div>
              )}
            </Reveal>
            <div className="col-span-12 lg:col-span-6">
              <Reveal delay={0.1}><p className="text-[10px] tracking-[0.4em] uppercase text-black/40 mb-5">{vendor.categories?.join(' · ')}</p></Reveal>
              <Reveal delay={0.16}><h1 className="font-presti text-4xl sm:text-5xl leading-[1.05]" data-testid="vendor-name">{vendor.name}</h1></Reveal>
              <Reveal delay={0.22} className="flex flex-wrap gap-x-6 gap-y-2 mt-5 text-xs text-black/50">
                {vendor.location && <span className="inline-flex items-center gap-1.5"><MapPin size={12} /> {vendor.location}</span>}
                {vendor.events_shot > 0 && <span data-testid="vendor-events-shot">{vendor.events_shot} weddings together</span>}
              </Reveal>
              {vendor.short_description && <Reveal delay={0.28}><p className="text-black/70 text-lg leading-relaxed mt-8">{vendor.short_description}</p></Reveal>}
              {vendor.long_description && <Reveal delay={0.32}><p className="text-black/55 leading-relaxed mt-5 whitespace-pre-line">{vendor.long_description}</p></Reveal>}
              <Reveal delay={0.36} className="flex flex-col sm:flex-row flex-wrap gap-3 mt-10">
                {website && (
                  <Magnetic>
                    <a href={website} target="_blank" rel="noopener noreferrer sponsored" onClick={() => trackVendorClick(vendor.id, 'website')} className="inline-flex items-center gap-3 px-8 py-4 bg-black text-white text-xs tracking-[0.25em] uppercase hover:bg-black/85 transition-colors" data-testid="vendor-website-btn">
                      Visit website <ArrowUpRight size={14} />
                    </a>
                  </Magnetic>
                )}
                {vendor.instagram_url && (
                  <a href={vendor.instagram_url} target="_blank" rel="noopener noreferrer" onClick={() => trackVendorClick(vendor.id, 'instagram')} className="inline-flex items-center justify-center gap-2 px-8 py-4 border border-black/20 text-xs tracking-[0.25em] uppercase hover:bg-black hover:text-white hover:border-black transition-colors" data-testid="vendor-instagram-btn">
                    <Instagram size={14} /> Instagram
                  </a>
                )}
                {vendor.phone && (
                  <a href={`tel:${vendor.phone}`} onClick={() => trackVendorClick(vendor.id, 'phone')} className="inline-flex items-center justify-center gap-2 px-8 py-4 border border-black/20 text-xs tracking-[0.25em] uppercase hover:bg-black hover:text-white hover:border-black transition-colors" data-testid="vendor-phone-btn">
                    <Phone size={14} /> Call
                  </a>
                )}
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {others.length > 0 && (
        <section className="px-6 md:px-12 pb-24 border-t border-black/5 pt-16">
          <div className="max-w-6xl mx-auto">
            <Reveal><h2 className="font-presti text-2xl md:text-3xl mb-10">More trusted vendors</h2></Reveal>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">{others.map((o) => <VendorCard key={o.id} vendor={o} />)}</div>
          </div>
        </section>
      )}
    </main>
  );
}
